import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook to fetch a platform setting value.
 * Returns the setting value or a default if not found.
 */
export function usePlatformSetting(settingKey: string, defaultValue: string = '') {
  const { data: settingValue, isLoading } = useQuery({
    queryKey: ['platform-settings', settingKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('setting_value')
        .eq('setting_key', settingKey)
        .maybeSingle();

      if (error) {
        console.warn(`Failed to fetch platform setting ${settingKey}:`, error);
        return defaultValue;
      }

      return data?.setting_value || defaultValue;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: 1,
    placeholderData: defaultValue,
  });

  return {
    value: settingValue || defaultValue,
    isLoading,
  };
}

/**
 * Hook to check if a boolean platform setting is enabled.
 */
export function usePlatformSettingBoolean(settingKey: string, defaultValue: boolean = false) {
  const defaultStr = defaultValue ? 'true' : 'false';
  const { value, isLoading } = usePlatformSetting(settingKey, defaultStr);
  
  // Check if value is explicitly 'true', otherwise return false (including 'false', '', null, undefined)
  const enabled = value === 'true' || value === '1' || value === 'yes';
  
  return {
    enabled,
    isLoading,
    rawValue: value, // For debugging
  };
}
